import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { ETaxKtbDocumentStatus } from "../../types/e-tax.type";
import { getKtbDocumentStatusLabel } from "./documentMeta";

/** The one bill a withdrawal is about. */
export interface WithdrawTarget {
  transactionUuid: string;
  transactionNo: string;
  customerName: string;
  documentStatus: ETaxKtbDocumentStatus | null;
}

interface CancelSubmissionModalProps {
  target: WithdrawTarget | null;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const Fact = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 96 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Confirmation for withdrawing one document from the filing agent.
 *
 * This is the only action on these screens that reaches out and un-does something at KTB,
 * so the dialog says which bill and what will happen to it rather than asking a bare
 * "are you sure?". Two things in particular are spelled out, because getting either wrong
 * is expensive: the bill comes back as ค้างส่ง and has to be sent again — a withdrawal is
 * not a way to make a bill go away — and the window can already have closed at KTB, in
 * which case nothing is withdrawn and KTB's own reason comes back as the error.
 */
export default function CancelSubmissionModal({
  target,
  loading,
  onCancel,
  onConfirm,
}: CancelSubmissionModalProps) {
  return (
    <Dialog open={Boolean(target)} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        ยกเลิกการนำส่งตัวแทน
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {target && (
          <>
            <Fact label="เลขที่บิล" value={target.transactionNo} />
            <Fact label="ลูกค้า" value={target.customerName || "-"} />
            <Fact
              label="สถานะล่าสุด"
              value={getKtbDocumentStatusLabel(target.documentStatus)}
            />

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.background.default,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                ระบบจะส่งคำขอยกเลิกไปยังตัวแทนนำส่ง เมื่อสำเร็จ
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  บิลนี้จะกลับไปอยู่ <b>ค้างส่ง</b> และส่งใหม่ได้
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  ประวัติการส่งครั้งนี้ยังอยู่ ไม่ได้ถูกลบ
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  ถ้าเอกสารถูกยื่นกรมสรรพากรไปแล้ว ตัวแทนนำส่งจะไม่ให้ยกเลิก
                  และจะแจ้งเหตุผลกลับมา
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onCancel} disabled={loading}>
          ปิด
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} loading={loading}>
          ยืนยันยกเลิกการนำส่ง
        </Button>
      </DialogActions>
    </Dialog>
  );
}
